import React, { useEffect, useMemo } from 'react';
import {
  Col, Row, Input,
  Form,
  Modal,
  notification,
  Upload,
  Button,
} from 'antd';
import { useNavigate } from 'react-router-dom';

import './ServiceInvoice.scss';
import { UploadOutlined } from '@ant-design/icons';
import { UploadChangeParam } from 'antd/lib/upload/interface';
import {
  post, RequestWrapper, isSuperAgentError, SuperAgentError, del,
} from '../../../dataSource/requests';

type TInvoice = {
  nfeNumber: string,
  billingValue: string,
  bp: string,
  companyName: string,
  emissionDate: string,
  id: number,
  invoiceNumber: string,
  landFieldName: string,
  pdf: number,
  xml: number,
  pdfName: string
  xmlName: string
}

type TPayload = {
  invoiceNumber: string
  nfeNumber: string,
  invoiceFiles: Array<File>
}

type Tform = {
  pdf: UploadChangeParam | undefined
  xml: UploadChangeParam | undefined
  invoiceNumber: string
  nfeNumber: string,
}
interface IModalForm {
    open: boolean;
    modalStatusChange: React.Dispatch<React.SetStateAction<boolean>>;
    invoice: TInvoice
    updateTable:()=> Promise<void>
  }

const ModalForm = ({
  open, modalStatusChange, invoice, updateTable,
}:IModalForm): JSX.Element => {
  const [formInstance] = Form.useForm<Tform>();
  const navigate = useNavigate();

  const disableForm:boolean = useMemo(() => !!invoice?.nfeNumber, [invoice]);

  const initialValues = useMemo(() => {
    if (invoice.nfeNumber && disableForm) {
      return {
        pdf: undefined,
        xml: undefined,
        invoiceNumber: invoice.invoiceNumber,
        nfeNumber: invoice.nfeNumber,
      };
    }
    return {
      pdf: undefined,
      xml: undefined,
      invoiceNumber: invoice.invoiceNumber,
      nfeNumber: '',
    };
  }, [invoice, disableForm]);

  // eslint-disable-next-line max-len
  const createInvoice = async (values:TPayload):Promise<RequestWrapper | SuperAgentError | Error> => {
    try {
      const formData = new FormData();

      values.invoiceFiles.forEach((element) => {
        if (element) {
          formData.append('nfeFiles', element);
        }
      });

      formData.append('nfeNumber', values.nfeNumber);
      formData.append('invoiceNumber', values.invoiceNumber);

      const requestBack: RequestWrapper = post({
        url: '/invoice/uploadNfeFiles',
        params: formData,

        frontConfigs: {
          navigateFunction: navigate,
        },
      });
      return await requestBack;
    } catch (error) {
      if (isSuperAgentError(error)) {
        return error.response;
      }
      return error as Error;
    }
  };

  const onSubmitForm = async (values:Tform): Promise<void> => {
    let response = { } as Promise<RequestWrapper | SuperAgentError | Error> | any;

    const payload = {
      ...values,
      invoiceFiles: [values.pdf?.file?.originFileObj, values.xml?.file?.originFileObj],
    } as TPayload;

    response = await createInvoice(payload);

    switch (true) {
      case (response != null && response?.body?.code === 9000): {
        notification.success({
          message: 'Sucesso',
          description: 'Cadastro efetuado com sucesso!',
        });
        modalStatusChange(false);
        updateTable();
        break;
      }

      case (response?.body?.code === -9001): {
        notification.warning({
          message: 'Atenção',
          description: 'O tipo de arquivo não é aceito no sistema. Somente são permitidos arquivos nos formatos PDF e XML.',
        });
        break;
      }

      case (response?.body?.code === -25003): {
        notification.warning({
          message: 'Atenção',
          description: 'A nota fiscal não foi encontrada no SAP.',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  const deleteNFandFiles = async ():Promise<void> => {
    let response = { } as Promise<RequestWrapper | SuperAgentError | Error> | any;

    try {
      const requestBack: RequestWrapper = del({
        url: `/invoice/deleteNfe/${invoice.invoiceNumber}`,
        frontConfigs: {
          navigateFunction: navigate,
        },
      });
      response = await requestBack;
    } catch (error) {
      if (isSuperAgentError(error)) {
        response = error.response;
      }
      response = error as Error;
    }

    switch (true) {
      case (response?.body?.code === 9016): {
        notification.success({
          message: 'Sucesso',
          description: 'Exclusão dos arquivos e número da nota fiscal, realizados com sucesso!',
        });
        modalStatusChange(false);
        updateTable();
        break;
      }

      case (response?.body?.code === -4003): {
        notification.warning({
          message: 'Atenção',
          description: 'Você não tem permissão para concluir essa operação!',
        });
        break;
      }

      default:
        notification.error({
          message: 'Erro',
          description: 'Desculpe, houve um erro, por favor tente novamente mais tarde.',
        });
        break;
    }
  };

  const showDelete = (): void => {
    Modal.confirm({
      title: 'Tem certeza que deseja excluir os arquivos e número da nota fiscal?',
      okText: 'Sim, deletar',
      okType: 'danger',
      cancelText: 'Não, cancelar',
      onOk: () => deleteNFandFiles(),
      onCancel: (): void => undefined,
    });
  };

  useEffect(() => {
    formInstance.setFieldsValue(initialValues);
  }, [formInstance, initialValues]);

  return (
    <Form
      scrollToFirstError
      layout="vertical"
      form={formInstance}
      name="form"
      initialValues={initialValues}
    >
      <Modal
        className="InvoiceModal"
        visible={open}
        okText={disableForm ? 'Excluir' : 'Salvar'}
        okButtonProps={{
          danger: disableForm,
        }}
        onOk={():void => {
          formInstance
            .validateFields()
            .then((values) => {
              if (disableForm) {
                formInstance.resetFields();
                showDelete();
              } else {
                formInstance.resetFields();
                onSubmitForm(values);
              }
            })
            .catch(() => {
              notification.error({
                message: 'Erro',
                description: 'Desculpe, existe erros no formulário!',
              });
            });
        }}
        onCancel={(): void => {
          modalStatusChange(false);
        }}
      >
        <Row
          gutter={[8, 24]}
          className="form-modal"
        >
          <Col xs={{ span: 24 }} lg={{ span: 20 }}>
            <Form.Item
              name="invoiceNumber"
              hidden
            >
              <Input type="hidden" />
            </Form.Item>
            <Form.Item
              label="Número da Nota Fiscal de Serviço"
              name="nfeNumber"
              rules={[
                { required: true },
              ]}
            >
              <Input
                style={{ width: '100%' }}
                placeholder="Digite "
                disabled={disableForm}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row
          gutter={[8, 24]}
          className="form-modal"
        >
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            {disableForm ? (
              <Form.Item label="Documento PDF">
                <span>
                  { invoice.pdfName}
                </span>
              </Form.Item>
            ) : (
              <Form.Item label="Documento PDF" name="pdf">
                <Upload
                  id="docFilePdf"
                  accept=".pdf,application/pdf"
                  disabled={disableForm}
                  maxCount={1}
                >
                  <Button>
                    <UploadOutlined />
                    Carregar arquivo pdf
                  </Button>
                </Upload>
              </Form.Item>

            )}
          </Col>
        </Row>
        <Row
          gutter={[8, 24]}
          className="form-modal"
        >
          <Col xs={{ span: 24 }} lg={{ span: 12 }}>
            {disableForm ? (
              <Form.Item label="Documento XML">
                <span>
                  { invoice.xmlName || '-'}
                </span>
              </Form.Item>
            ) : (
              <Form.Item label="Documento XML" name="xml">
                <Upload
                  id="docFileXml"
                  accept=".xml,application/xml"
                  disabled={disableForm}
                  maxCount={1}
                >
                  <Button>
                    <UploadOutlined />
                    Carregar arquivo xml
                  </Button>
                </Upload>
              </Form.Item>
            )}
          </Col>
        </Row>
      </Modal>
    </Form>
  );
};

export default ModalForm;
